import {
  useMsal,
  useIsAuthenticated as useMsalIsAuthenticated,
} from "@azure/msal-react";
import {
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  onAuthStateChanged,
} from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useBreakpointValue } from "@chakra-ui/react";

import { auth, db } from "../firebase";
import { powerBiScopes } from "../Utils/authConfig";
import { logUserAction } from "../Utils/apiUtils";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const { instance } = useMsal();
  const msalIsAuthenticated = useMsalIsAuthenticated();

  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserDetails, setCurrentUserDetails] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsUser, setViewAsUser] = useState(null);

  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const logAndUpdateLogin = async (email, displayName, provider) => {
    const userQuery = query(
      collection(db, "users"),
      where("email", "==", email)
    );
    const snapshot = await getDocs(userQuery);
    const userDoc = snapshot.docs[0];
    const userData = userDoc?.data();

    const user = {
      email,
      displayName: userData?.name || displayName,
      provider,
    };

    setCurrentUser(user);
    logUserAction(user, "signed_in");

    if (userDoc?.id) {
      await updateLastLogin(userDoc.id);
    }
  };

  // On initial mount - check for MSAL accounts
  useEffect(() => {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      const user = {
        email: account.username,
        displayName: account.name,
        provider: "msal",
      };
      setCurrentUser(user);
      setAccount(account);
      logUserAction(user, "signed_in");

      // Fetch Firestore ID and update lastLogin
      const fetchAndUpdate = async () => {
        const userQuery = query(
          collection(db, "users"),
          where("email", "==", user.email)
        );
        const snapshot = await getDocs(userQuery);
        const userDoc = snapshot.docs[0];
        if (userDoc?.id) {
          await updateLastLogin(userDoc.id);
        }
      };

      fetchAndUpdate();
    }
    setLoading(false);
  }, [instance]);

  // Handle MSAL redirect login
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          const user = {
            email: response.account.username,
            displayName: response.account.name,
            provider: "msal",
          };
          setCurrentUser(user);
          setAccount(response.account);
          logUserAction(user, "signed_in");
        }
      } catch (err) {
        console.error("Redirect error:", err);
      }
    };

    handleRedirect();
  }, [instance]);

  // When currentUser is set, fetch Firestore profile
  useEffect(() => {
    if (!currentUser?.email) return;

    const userQuery = query(
      collection(db, "users"),
      where("email", "==", currentUser.email)
    );

    const unsubscribe = onSnapshot(
      userQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setCurrentUserDetails({ id: doc.id, ...doc.data() });
        } else {
          console.warn("User not found in Firestore:", currentUser.email);
          setCurrentUserDetails(null);
        }
      },
      (error) => {
        console.error("Error listening to user details:", error);
      }
    );

    return () => unsubscribe(); // Cleanup on unmount or email change
  }, [currentUser?.email]);

  // Login handlers
  const handleLogin = async () => {
    try {
      const response = isMobile
        ? instance.loginRedirect(powerBiScopes)
        : await instance.loginPopup(powerBiScopes);

      const user = {
        email: response.account.username,
        displayName: response.account.name,
        provider: "msal",
      };

      setCurrentUser(user);
      setAccount(response.account);
      logUserAction(user, "signed_in");
    } catch (error) {
      console.error("MSAL login failed:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await logAndUpdateLogin(user.email, user.displayName, "firebase");
      }
    });

    return () => unsubscribe();
  }, []);

  const updateLastLogin = async (userId) => {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      lastLogin: new Date().toISOString(),
    });
  };

  const handleLoginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await logAndUpdateLogin(
        result.user.email,
        result.user.displayName,
        "firebase"
      );
    } catch (error) {
      console.error("Google login failed:", error);
    }
  };

  const handleLoginWithEmailAndPassword = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      const userQuery = query(
        collection(db, "users"),
        where("email", "==", email)
      );
      const snapshot = await getDocs(userQuery);
      const userDoc = snapshot.docs[0];
      const userData = userDoc?.data();

      const user = {
        email,
        displayName: userData?.name || result.user.displayName,
        provider: "firebase",
      };

      setCurrentUser(user);
      logUserAction(user, "signed_in");

      if (userDoc?.id) {
        await updateLastLogin(userDoc.id);
      }
    } catch (error) {
      console.error("Email login failed:", error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await firebaseSignOut(auth);
      if (msalIsAuthenticated && account) {
        await instance.logoutPopup({ account });
        setAccount(null);
      }

      setCurrentUser(null);
      setCurrentUserDetails(null);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Expose enriched values
  const clientId = currentUserDetails?.clientId || null;
  const isAdmin = currentUserDetails?.isAdmin === true;

  const authContextValue = useMemo(
    () => ({
      handleLogin,
      handleLoginWithGoogle,
      handleLoginWithEmailAndPassword,
      handleLogout,
      currentUser,
      currentUserDetails,
      isAuthenticated: !!currentUser || msalIsAuthenticated,
      loading,
      account,
      clientId,
      isAdmin,
      setCurrentUser,
    }),
    [
      currentUser,
      currentUserDetails,
      msalIsAuthenticated,
      loading,
      account,
      clientId,
      isAdmin,
      setCurrentUser,
    ]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
